import React, { useEffect, useCallback } from 'react';
import { Mic, MicOff, X, Loader2, Camera } from 'lucide-react';
import { useVoice } from '../hooks/useVoice';
import { useCamera } from '../hooks/useCamera';

interface VoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResult: (transcript: string) => void;
  onCameraCapture?: (file: File) => void;
}

export const VoiceModal: React.FC<VoiceModalProps> = ({
  isOpen,
  onClose,
  onResult,
  onCameraCapture,
}) => {
  const {
    isListening,
    transcript,
    interimTranscript,
    error,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
  } = useVoice();

  const { capture: capturePhoto, isCapturing } = useCamera();

  // Handle camera capture
  const handleCameraCapture = useCallback(async () => {
    stopListening();
    const file = await capturePhoto();
    if (file && onCameraCapture) {
      onCameraCapture(file);
      onClose();
    }
  }, [capturePhoto, onCameraCapture, onClose, stopListening]);

  // Start listening when modal opens
  useEffect(() => {
    if (isOpen && isSupported) {
      resetTranscript();
      // Small delay to allow modal animation
      const timer = setTimeout(() => startListening(), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isSupported, startListening, resetTranscript]);

  // Handle close
  const handleClose = useCallback(() => {
    stopListening();
    onClose();
  }, [stopListening, onClose]);

  // Submit when user stops speaking (transcript is final)
  useEffect(() => {
    if (!isListening && transcript && isOpen) {
      // Give user a moment to see the transcript
      const timer = setTimeout(() => {
        onResult(transcript);
        handleClose();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isListening, transcript, isOpen, onResult, handleClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const displayText = transcript + interimTranscript;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-sm bg-stone-800 rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 flex flex-col items-center">
          {/* Not supported message */}
          {!isSupported ? (
            <div className="text-center">
              <MicOff className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                Voice Not Available
              </h3>
              <p className="text-white/60 text-sm">
                Your browser doesn't support voice input. Try Chrome, Safari, or Edge.
              </p>
            </div>
          ) : (
            <>
              {/* Mic indicator */}
              <div
                className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 transition-all duration-300 ${
                  isListening
                    ? 'bg-amber-500 animate-pulse shadow-lg shadow-amber-500/50'
                    : error
                    ? 'bg-red-500/20 border-2 border-red-500'
                    : 'bg-white/10'
                }`}
              >
                {isListening ? (
                  <Mic className="w-10 h-10 text-stone-900" />
                ) : error ? (
                  <MicOff className="w-10 h-10 text-red-400" />
                ) : (
                  <Loader2 className="w-10 h-10 text-white/60 animate-spin" />
                )}
              </div>

              {/* Status text */}
              <h3 className="text-lg font-semibold text-white mb-2">
                {isListening
                  ? 'Listening...'
                  : error
                  ? 'Error'
                  : 'Processing...'}
              </h3>

              {/* Transcript display */}
              <div className="w-full min-h-[60px] p-4 bg-white/5 rounded-xl border border-white/10">
                {error ? (
                  <p className="text-red-400 text-sm text-center">{error}</p>
                ) : displayText ? (
                  <p className="text-white text-center">
                    {transcript}
                    <span className="text-white/50">{interimTranscript}</span>
                  </p>
                ) : (
                  <p className="text-white/40 text-sm text-center italic">
                    Try: "Find a medium cigar with coffee notes"
                  </p>
                )}
              </div>

              {/* Retry button on error */}
              {error && (
                <button
                  onClick={() => {
                    resetTranscript();
                    startListening();
                  }}
                  className="mt-4 px-6 py-2 bg-amber-500 text-stone-900 rounded-full font-medium hover:bg-amber-400 transition-colors"
                >
                  Try Again
                </button>
              )}

              {/* Manual stop button */}
              {isListening && (
                <button
                  onClick={stopListening}
                  className="mt-4 px-6 py-2 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-colors"
                >
                  Done Speaking
                </button>
              )}

              {/* Camera option */}
              {onCameraCapture && (
                <div className="mt-6 pt-4 border-t border-white/10 w-full">
                  <button
                    onClick={handleCameraCapture}
                    disabled={isCapturing}
                    className="flex items-center gap-3 px-4 py-3 w-full rounded-xl bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-50"
                  >
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Camera className="w-5 h-5 text-amber-500" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-medium">
                        {isCapturing ? 'Opening camera...' : 'Identify a Cigar'}
                      </p>
                      <p className="text-white/50 text-sm">Snap a photo of the band</p>
                    </div>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Hint */}
        <div className="px-8 pb-6">
          <p className="text-white/40 text-xs text-center">
            Tip: Speak naturally. Try "What's today's smoke?" or "Identify this cigar"
          </p>
        </div>
      </div>
    </div>
  );
};

export default VoiceModal;
